<script setup lang="ts">
import {
    Download,
    MousePointerClick,
    Link2,
    Table,
    PenLine,
    Cloud,
    FileText,
    Layers,
    Sparkles,
    ShieldCheck,
    Zap,
    BarChart3,
} from 'lucide-vue-next';
import type { LucideIcon } from 'lucide-vue-next';
import { markRaw } from 'vue';
import type { Component } from 'vue';
import LazyScene from './LazyScene.vue';
import AssociationScene from './scenes/AssociationScene.vue';
import ExtractionScene from './scenes/ExtractionScene.vue';
import ImportScene from './scenes/ImportScene.vue';
import RedactionScene from './scenes/RedactionScene.vue';
import ScreeningLine from './ScreeningLine.vue';

type Highlight = { icon: LucideIcon; text: string };
type Feature = {
    step: string;
    label: string;
    icon: LucideIcon;
    title: string;
    description: string;
    highlights: Highlight[];
    scene: Component;
    reversed: boolean;
};

const features: Feature[] = [
    {
        step: '01',
        label: 'Import',
        icon: Download,
        title: 'Vos références réunies au même endroit',
        description:
            'Connectez votre bibliothèque Zotero pour importer vos collections en un clic, ou déposez vos PDF directement. Fini les fichiers RIS éparpillés : tout arrive dans une bibliothèque unique, prête à être triée.',
        highlights: [
            {
                icon: Cloud,
                text: 'Connexion Zotero : collections, groupes et items',
            },
            { icon: FileText, text: 'Dépôt de PDF en masse ou un par un' },
            { icon: Layers, text: 'Toutes vos références centralisées' },
        ],
        scene: markRaw(ImportScene),
        reversed: false,
    },
    {
        step: '02',
        label: 'Tri des articles',
        icon: MousePointerClick,
        title: 'Triez vos articles un par un, avec justification',
        description:
            "Passez vos articles en revue et décidez d'inclure ou d'exclure chacun en notant votre justification. L'assistant peut proposer un premier tri, mais c'est toujours vous qui tranchez — et le diagramme PRISMA se construit tout seul.",
        highlights: [
            {
                icon: MousePointerClick,
                text: 'Inclure / exclure avec justification',
            },
            {
                icon: Sparkles,
                text: 'Suggestions IA optionnelles, vous décidez',
            },
            {
                icon: ShieldCheck,
                text: 'Chaque décision traçable et réversible',
            },
        ],
        scene: markRaw(ScreeningLine),
        reversed: true,
    },
    {
        step: '03',
        label: 'Association PDF',
        icon: Link2,
        title: 'Chaque PDF relié à sa référence, sans renommer de fichiers',
        description:
            'Rewio croise les métadonnées et le DOI de vos fichiers avec votre bibliothèque pour proposer les bonnes associations. Un simple glisser-déposer suffit pour ajuster les cas ambigus, et vous validez avant de lier.',
        highlights: [
            { icon: Zap, text: 'Correspondances par métadonnées & DOI' },
            { icon: Layers, text: 'Glisser-déposer pour les cas ambigus' },
            { icon: ShieldCheck, text: 'Validation visuelle avant de lier' },
        ],
        scene: markRaw(AssociationScene),
        reversed: false,
    },
    {
        step: '04',
        label: 'Extraction',
        icon: Table,
        title: 'Des données structurées, un diagramme généré',
        description:
            "Remplissez une grille d'extraction déjà organisée et personnalisable selon vos colonnes. L'IA peut pré-remplir depuis le texte intégral, vous vérifiez et corrigez. Le diagramme PRISMA 2020 se génère automatiquement.",
        highlights: [
            { icon: Table, text: 'Grille pré-organisée et personnalisable' },
            { icon: Sparkles, text: 'Pré-remplissage assisté, vous vérifiez' },
            { icon: BarChart3, text: 'Diagramme PRISMA + export CSV / Excel' },
        ],
        scene: markRaw(ExtractionScene),
        reversed: true,
    },
    {
        step: '05',
        label: 'Rédaction',
        icon: PenLine,
        title: 'Rédigez votre revue, au même endroit',
        description:
            "L'éditeur intégré vous laisse rédiger votre revue de littérature en y insérant directement le diagramme PRISMA et la grille d'extraction. Plus de copier-coller entre logiciels — votre revue se construit d'un bout à l'autre dans Rewio.",
        highlights: [
            { icon: PenLine, text: 'Éditeur intégré pour rédiger' },
            { icon: BarChart3, text: 'Insérez le diagramme PRISMA' },
            { icon: Table, text: "Insérez la grille d'extraction" },
        ],
        scene: markRaw(RedactionScene),
        reversed: false,
    },
];
</script>

<template>
    <section id="features" class="border-t border-border/40 py-24">
        <div class="mx-auto max-w-5xl px-4">
            <div class="text-center">
                <span
                    class="mb-4 inline-block text-xs font-medium tracking-widest text-brand uppercase"
                >
                    Comment ça marche
                </span>
                <h2
                    class="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
                >
                    De vos sources à votre revue, en 5 étapes
                </h2>
                <p class="mx-auto mt-4 max-w-xl text-muted-foreground">
                    Toutes les étapes de votre revue de littérature, réunies
                    dans un seul outil. Voici chacune en action.
                </p>
            </div>

            <div class="mt-14 space-y-16 sm:mt-20 sm:space-y-24">
                <div
                    v-for="feature in features"
                    :key="feature.step"
                    class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
                >
                    <!-- Text side -->
                    <div :class="feature.reversed ? 'lg:order-2' : ''">
                        <span
                            class="mb-3 inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-xs font-medium text-brand"
                        >
                            <component :is="feature.icon" class="h-3.5 w-3.5" />
                            Étape {{ feature.step }} · {{ feature.label }}
                        </span>

                        <h3
                            class="mt-4 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
                        >
                            {{ feature.title }}
                        </h3>

                        <p
                            class="mt-5 text-base leading-relaxed text-muted-foreground"
                        >
                            {{ feature.description }}
                        </p>

                        <ul class="mt-8 space-y-4">
                            <li
                                v-for="highlight in feature.highlights"
                                :key="highlight.text"
                                class="flex items-start gap-3"
                            >
                                <div
                                    class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10"
                                >
                                    <component
                                        :is="highlight.icon"
                                        class="h-4 w-4 text-brand"
                                    />
                                </div>
                                <span
                                    class="mt-1 text-sm text-muted-foreground"
                                >
                                    {{ highlight.text }}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <!-- Animated scene side -->
                    <div :class="feature.reversed ? 'lg:order-1' : ''">
                        <LazyScene :is="feature.scene" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
